import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Reducers} from '@app/reducers';
import {AssociationDialogComponent} from '@clients/components/associations/association-dialog/association-dialog.component';
import {AssociationViewDialogComponent} from '@clients/components/associations/association-view-dialog/association-view-dialog.component';
import * as associationActions from '@clients/ngrx/actions/associations.actions';
import {ConfigService} from '@config/config.service';
import {ContactDialogComponent} from '@crm/components/contacts/contact-dialog/contact-dialog.component';
import {CrmTaskDialogComponent} from '@crm/components/leads/crm-task-dialog/crm-task-dialog.component';
import * as contactsActions from '@crm/ngrx/actions/contacts.actions';
import * as leadsBoardTasksActions from '@crm/ngrx/actions/leads-board.actions';
import * as contractActions from '@billing//ngrx/actions/contracts.actions';
import * as leadsListTasksActions from '@crm/ngrx/actions/leads-list.actions';
import * as projectsActions from '@crm/ngrx/actions/projects.actions';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import {Actions, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {ContactResponse} from '@services/associations/model/contactResponse';
import {PageContactResponse} from '@services/associations/model/pageContactResponse';
import {PageLeadResponse} from '@services/associations/model/pageLeadResponse';
import {PageStreetDto} from '@services/associations/model/pageStreetDto';
import {PageTaskResponse} from '@services/associations/model/pageTaskResponse';
import {ProjectResponse} from '@services/associations/model/projectResponse';
import {StreetDto} from '@services/associations/model/streetDto';
import {TaskResponse} from '@services/associations/model/taskResponse';
import {AddStreetDialogComponent} from '@shared/components/add-street-dialog/add-street-dialog.component';
import {ConfirmationDialogComponent} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {ListItemType} from '@shared/enum/list-item-type';
import {PaginationModel} from '@shared/models/pagination.model';
import {PermissionsModel} from '@shared/models/permission.model';
import {AssociationsService} from '@shared/services/associations.service';
import {CrmService} from '@shared/services/crm.service';
import {DictionariesService} from '@shared/services/dictionaries.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, debounceTime, EMPTY, Observable, Subject, takeUntil} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {PageResponseProjectResponse} from "@services/associations/model/pageResponseProjectResponse";
import {BillingService} from "@shared/services/billing.service";
import {PageBillingCounterpartyModel} from "@services/back-office/model/pageBillingCounterpartyModel";
import {BillingCounterpartyModel} from "@services/back-office/model/billingCounterpartyModel";
import {PageResponseBillingContractModel} from "@services/back-office/model/pageResponseBillingContractModel";
import {PageBillingActModel} from "@services/back-office/model/pageBillingActModel";
import {PageBillingTransactionModel} from "@services/back-office/model/pageBillingTransactionModel";
import {PageAssociationListResponse} from "@services/back-office/model/pageAssociationListResponse";
import {PageNameResponse} from '@services/associations/model/pageNameResponse';
import {MatTableDataSource} from '@angular/material/table';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-list-dialog',
    templateUrl: './list-dialog.component.html',
    styleUrls: ['./list-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ListDialogComponent implements OnInit, OnDestroy {
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();
    /** Loading State */
    loading$ = new BehaviorSubject<boolean>(true);
    searching$ = new BehaviorSubject<boolean>(false);
    /** Form */
    filterForm: UntypedFormGroup;
    /** Controls props */
    canAddItem = false;
    canEditItem = false;
    canDeleteItem = false;
    canOpenItem = false;
    canSearchItem = true;
    /** Table */
    dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    columns: string[] = [];
    columnStyles: string[] = [];
    columnTranslations: string[] = [];
    /** Pagination Settings */
    pagination: PaginationModel = {
        first: true,
        last: false,
        number: 0,
        numberOfElements: 0,
        size: new ConfigService().paginationConfig.pageSize,
        totalElements: 0,
    };
    pageSizeOptions = this.config.paginationConfig.pageSizeOptions;

    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;

    manager = new UntypedFormControl();

    /** Multi-select */
    multiple = false;
    selectedItems: any[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            itemsType: ListItemType,
            selector?: any,
            searchDisabled?: boolean,
            props?: any,
            actions?: any,
            multiple?: boolean
        },
        public dialogRef: MatDialogRef<ListDialogComponent>,
        private config: ConfigService,
        private formBuilder: UntypedFormBuilder,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private billingService: BillingService,
        private dictionariesService: DictionariesService,
        private sharedService: SharedService,
        private changeDetectorRef: ChangeDetectorRef,
        private associationService: AssociationsService,
        private crmService: CrmService,
        private store$: Store<Reducers>,
        private actions$: Actions,
    ) {
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        this.multiple = !!this.data.multiple;
        this.getPermissions();
        this.initForm();
        this.getItemsList();
        if (!this.data.searchDisabled || this.data.selector) {
            this.watchFormChanged();
        }
        /** Watch Delete Success Actions */
        this.watchDeleteSuccess();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
        this.searching$.complete();
    }

    getPermissions() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
            this.permissions = permissions;
            this.setupList();
        });
    }

    checkPermissions(permission: string): boolean {
        return this.permissions ?
            SharedService.checkPermission(permission, this.permissions[0].permissions, this.permissions[0].permissionsExists) :
            false;
    }

    setupList() {
        switch (this.data.itemsType) {
            case ListItemType.COUNTERPARTY:
                this.columns = ['code', 'name', 'address', 'actions'];
                this.columnStyles = ['w150px', 'grow', 'grow', 'w200px'];
                this.canAddItem = !!(this.checkPermissions('BILLING_NEW_EDIT') && this.data?.actions?.canAddItem);
                this.canEditItem = !!(this.checkPermissions('BILLING_NEW_EDIT') && this.data?.actions?.canEditItem);
                this.canDeleteItem = !!(this.checkPermissions('BILLING_NEW_EDIT') && this.data?.actions?.canDeleteItem);
                break;
            case ListItemType.ACT:
                this.columns = ['name', 'contractOwnerName', 'uncovered', 'amount', 'actions'];
                this.columnStyles = ['grow', 'grow', 'w120px', 'w120px', 'w200px'];
                this.canEditItem = !!(this.checkPermissions('BILLING_NEW_ACT_EDIT') && this.data?.actions?.canEditItem);
                break;
            case ListItemType.CONTRACT:
                this.columns = ['number', 'name', 'type', 'status', 'actions'];
                this.columnStyles = ['w150px', 'grow', 'w150px', 'w150px', 'w200px'];
                this.columnTranslations = ['', '', 'modules.billing.agreements.types', 'modules.billing.agreements.statuses'];
                this.canEditItem = !!(this.checkPermissions('BILLING_NEW_EDIT') && this.data?.actions?.canEditItem);
                // picking a contract means picking among a client's several, which differ by type: offer that filter unasked
                this.data.selector = this.data.selector ?? {
                    translation: 'modules.billing.agreements.types',
                    options: ['SAAS', 'ACCOUNTING', 'PERSONAL_ACCOUNTING', 'BUSINESS_SAAS', 'BUSINESS_ACCOUNTING']
                };
                break;
            case ListItemType.TRANSACTION:
                this.columns = ['name', 'handled', 'amount', 'comment'];
                this.columnStyles = ['w250px', 'w120px', 'w120px', 'grow'];
                break;
            case ListItemType.STREET:
                this.columns = ['type', 'name', 'actions'];
                this.columnStyles = ['w150px', 'grow', 'w120px'];
                this.columnTranslations = ['common.streetTypes'];
                this.canAddItem = true;
                this.canEditItem = true;
                this.canDeleteItem = true;
                break;
            case ListItemType.ASSOCIATION:
                this.columns = ['type', 'name', 'address', 'actions'];
                this.columnStyles = ['w150px', 'grow', 'grow', 'w120px'];
                this.columnTranslations = ['modules.clients.associations.types'];

                this.watchAddEditAssociationSuccess();
                break;
            case ListItemType.CONTACT:
                this.columns = ['lastName', 'firstName', 'parentalName', 'type', 'actions'];
                this.columnStyles = ['grow', 'grow', 'grow', 'grow', 'w120px'];
                this.columnTranslations = ['', '', '', 'modules.crm.contacts.types'];

                this.canAddItem = true;
                this.canEditItem = true;
                this.canDeleteItem = true;
                this.watchAddEditContactSuccess();
                break;
            case ListItemType.PROJECT:
                this.columns = ['name', 'manager', 'responsible', 'status', 'state', 'actions'];
                this.columnStyles = ['grow', 'grow', 'grow', 'grow', 'grow', 'w120px'];
                this.columnTranslations = ['', '', '', 'modules.crm.projects.statuses', 'modules.crm.projects.states'];

                this.canAddItem = true;
                this.canEditItem = true;
                this.canDeleteItem = true;
                this.watchAddEditProjectSuccess();
                break;
            case ListItemType.TASK:
                this.columns = ['dateTime', 'manager', 'taskType', 'analytic', 'actions'];

                this.canAddItem = true;
                this.canEditItem = true;
                this.canDeleteItem = true;
                this.watchAddEditTaskSuccess();
                break;
            case ListItemType.LEAD:
                this.columns = ['contact', 'type', 'manager', 'creationDate', 'actions'];
                this.canSearchItem = false;
                break;
            default:
                return;
        }
    }

    initForm() {
        this.filterForm = this.formBuilder.group({
            keyWord: [this.data?.props?.keyWord ? this.data?.props?.keyWord : ''],
            selector: []
        });
    }

    watchFormChanged() {
        if (!this.filterForm) {
            this.initForm();
        }

        this.filterForm.controls['keyWord'].valueChanges.pipe(
            takeUntil(this.destroyed$),
            debounceTime(this.config.searchTimeout)
        ).subscribe(() => {
            this.pagination.number = 0;
            this.dataSource.data = [];
            this.getItemsList();
        });

        this.filterForm.controls['selector'].valueChanges.pipe(
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.pagination.number = 0;
            this.dataSource.data = [];
            this.getItemsList();
        });
    }

    getItemsList() {
        switch (this.data.itemsType) {
            case ListItemType.CONTRACT:
                this.loadContractsList();
                break;
            case ListItemType.TRANSACTION:
                this.loadTransactionsList();
                break;
            case ListItemType.COUNTERPARTY:
                this.loadCounterparties();
                break;
            case ListItemType.STREET:
                this.loadStreets();
                break;
            case ListItemType.ASSOCIATION:
                this.loadAssociationsList();
                break;
            case ListItemType.CONTACT:
                this.loadContactsList();
                break;
            case ListItemType.PROJECT:
                this.loadProjectsList();
                break;
            case ListItemType.TASK:
                this.loadTasksList();
                break;
            case ListItemType.LEAD:
                this.loadLeadsList();
                break;
            case ListItemType.ACT:
                this.loadActs();
                break;
            default:
                return;
        }
    }

    /** Change Page */
    changePage(event: any) {
        this.pagination.size = event.pageSize;
        this.pagination.number = event.pageIndex;
        this.getItemsList();
    }

    getRequestQuery() {
        const formData = this.filterForm.getRawValue();
        return {
            ...formData.keyWord && {keyWord: formData.keyWord},
            ...formData.selector != undefined && true && {selector: formData.selector}
        };
    }

    loadTransactionsList() {
        this.searching$.next(true);
        this.billingService.loadPayments({...this.data.props, query: this.getRequestQuery()?.keyWord, handled: this.getRequestQuery()?.selector}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageBillingTransactionModel) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadActs() {
        this.searching$.next(true);
        this.billingService.loadActs({...this.data.props, query: this.getRequestQuery()?.keyWord}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageBillingActModel) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadCounterparties() {
        this.searching$.next(true);
        this.billingService.loadCounterparties({value: this.getRequestQuery()?.keyWord}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageBillingCounterpartyModel) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadContractsList() {
        this.searching$.next(true);
        const type = this.getRequestQuery()?.selector;

        this.billingService.loadContracts(
            {...this.data?.props, query: this.getRequestQuery()?.keyWord, ...type && {types: new Set([type])}},
            this.pagination
        ).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageResponseBillingContractModel) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadStreets() {
        this.dictionariesService.getStreets({name: this.getRequestQuery()?.keyWord, activeOnly: true, cityId: this.data.props.cityId}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            map((response: PageStreetDto) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadAssociationsList() {
        this.searching$.next(true);
        this.associationService.loadAssociations({query: this.getRequestQuery()?.keyWord}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageAssociationListResponse  | PageNameResponse) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadContactsList() {
        this.searching$.next(true);
        this.crmService.loadContacts({query: this.getRequestQuery()?.keyWord}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageContactResponse) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadProjectsList() {
        this.searching$.next(true);
        this.crmService.loadProjects({query: this.getRequestQuery()?.keyWord}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageResponseProjectResponse) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadTasksList() {
        this.searching$.next(true);
        this.crmService.loadLeadsListTasks({keyWord: this.getRequestQuery()?.keyWord}, this.pagination).pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageTaskResponse) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    loadLeadsList() {
        this.searching$.next(true);
        this.crmService.getLeads(this.pagination, this.manager?.value?.id ? this.manager?.value?.id : '').pipe(
            takeUntil(this.destroyed$),
            filter(data => !!data),
            map((response: PageLeadResponse) => {
                if (response && response.content) {
                    this.pagination = {
                        first: response.first,
                        last: response.last,
                        number: response.number,
                        numberOfElements: response.numberOfElements,
                        totalElements: response.totalElements,
                        size: response.size
                    };

                    this.dataSource.data = response.content;
                    this.changeDetectorRef.detectChanges();
                }

                this.loading$.next(false);
            }),
            catchError((error: HttpErrorResponse) => {
                SharedService.showErrorWithTitle(
                    this.translateService,
                    this.toastrService,
                    `responseErrors.${error.message}`
                );
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe();
    }

    addEditItem(itemToEdit?: any) {
        switch (this.data.itemsType) {
            case ListItemType.STREET:
                // eslint-disable-next-line no-case-declarations
                const streetDialog$ = this.sharedService.openDialog(
                    AddStreetDialogComponent,
                    {...this.config.addItemDialog, hasBackdrop: true, height: 'auto'},
                    {cityId: this.data.props.cityId, street: itemToEdit}
                );

                streetDialog$.afterClosed().pipe(
                    take(1),
                ).subscribe((dialogData) => {
                    if (!dialogData.result || !dialogData.data) {
                        return;
                    }
                    if (itemToEdit) {
                        const itemToEditIndex = this.dataSource.data.findIndex((street: StreetDto) => {
                            return street.id === itemToEdit.id;
                        });

                        if (itemToEditIndex !== -1) {
                            const streets = this.dataSource.data;

                            if (streets && streets.length) {
                                streets.splice(itemToEditIndex, 1, dialogData.data);
                                this.dataSource.data = streets;
                            } else {
                                this.dataSource.data = [dialogData.data];
                            }

                            this.changeDetectorRef.detectChanges();
                        }
                    } else {
                        this.closeDialog(true, dialogData.data);
                    }
                });
                break;
            case ListItemType.ASSOCIATION:
                this.upsertAssociation(itemToEdit);
                break;
            case ListItemType.CONTACT:
                this.upsertContact(itemToEdit);
                break;
            case ListItemType.PROJECT:
                this.upsertProject(itemToEdit);
                break;
            case ListItemType.TASK:
                this.upsertTask(itemToEdit);
                break;
            case ListItemType.COUNTERPARTY:
                this.upsertCounterParty(itemToEdit);
                break;
            default:
                return;
        }
    }

    upsertCounterParty(item: BillingCounterpartyModel) {
        if (item?.id) {
            this.sharedService.openCrmItem(ListItemType.COUNTERPARTY, item.id);
        } else {
            this.sharedService.createCrmItem(ListItemType.COUNTERPARTY, {});
        }

        this.actions$.pipe(
            ofType(contractActions.ContractsActionTypes.AddEditCounterPartySuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: contractActions.AddEditCounterPartySuccess) => {
            if (item) {
                const itemToEditIndex = this.dataSource.data.findIndex((counterparty: BillingCounterpartyModel) => {
                    return item.id === counterparty.id;
                });

                if (itemToEditIndex !== -1) {
                    const counterparties = this.dataSource.data;

                    if (counterparties && counterparties.length) {
                        counterparties.splice(itemToEditIndex, 1, action.payload);
                        this.dataSource.data = counterparties;
                    } else {
                        this.dataSource.data = [action.payload];
                    }

                    this.changeDetectorRef.detectChanges();
                }
            } else {
                this.closeDialog(true, action.payload);
            }
        });
    }

    /** Upsert Association */
    upsertAssociation(association: AssociationListResponse) {
        this.sharedService.loadLazyModule(() => {
            return import('../../../clients/clients.module').then(mod => mod.ClientsModule);
        });

        this.sharedService.openDialog(
            AssociationDialogComponent,
            {...this.config.addItemDialog, hasBackdrop: true},
            {
                edit: !!association,
                association: association
            }
        );
    }

    upsertContact(contact: ContactResponse) {
        this.sharedService.openDialog(
            ContactDialogComponent,
            {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
            {edit: !!contact, contactToEdit: contact}
        );
    }

    upsertProject(project: ProjectResponse) {
        if (project?.id) {
            this.sharedService.openCrmItem(ListItemType.PROJECT, project.id);
        } else {
            this.sharedService.createCrmItem(ListItemType.PROJECT);
        }
    }

    upsertTask(task: TaskResponse) {
        this.sharedService.openDialog(
            CrmTaskDialogComponent,
            {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
            {edit: true, task}
        );
    }

    deleteItem(item: any) {
        switch (this.data.itemsType) {
            case ListItemType.CONTACT:
                this.deleteConfirmation(
                    item.id,
                    'modules.crm.contacts.deleteContactConfirmation',
                    {name: `${item.lastName ? item.lastName : ''} ${item.firstName ? item.firstName : ''}`}
                );
                break;
            case ListItemType.PROJECT:
                this.deleteConfirmation(
                    item.id,
                    'modules.crm.projects.deleteProjectWarning',
                );
                break;
            case ListItemType.TASK:
                this.deleteConfirmation(
                    item.id,
                    'modules.crm.leads.list.deleteTaskWarning',
                );
                break;
            case ListItemType.COUNTERPARTY:
                this.deleteConfirmation(
                    item.id,
                    'Видалити компанію?',
                );
                break;
            default:
                return;
        }
    }

    deleteConfirmation(itemId: string, translation: string, interpolation?: Record<string, unknown>) {
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    translation,
                    interpolation ? interpolation : {}
                ),
                cancelText: 'common.buttons.cancel',
                submitText: 'common.buttons.delete',
            }
        );

        dialog$.afterClosed().pipe(
            take(1)
        ).subscribe((result) => {
            if (result) {
                switch (this.data.itemsType) {
                    case ListItemType.CONTACT:
                        this.store$.dispatch(new contactsActions.DeleteContact(itemId));
                        break;
                    case ListItemType.PROJECT:
                        this.store$.dispatch(new projectsActions.DeleteProjects(itemId));
                        break;
                    case ListItemType.TASK:
                        this.store$.dispatch(new leadsListTasksActions.DeleteTasks(itemId));
                        break;
                    case ListItemType.COUNTERPARTY:
                        this.billingService.deleteCounterparty(itemId).pipe(
                            takeUntil(this.destroyed$),
                            map(() => {
                                const itemToEditIndex = this.dataSource.data.findIndex((counterparty: BillingCounterpartyModel) => {
                                    return itemId === counterparty.id;
                                });

                                if (itemToEditIndex != -1) {
                                    const counterparties = this.dataSource.data;
                                    counterparties.splice(itemToEditIndex, 1);
                                    this.dataSource.data = counterparties;

                                    this.changeDetectorRef.detectChanges();
                                }
                            }),
                            catchError((error: HttpErrorResponse) => {
                                SharedService.showErrorWithTitle(
                                    this.translateService,
                                    this.toastrService,
                                    `responseErrors.${error.error.message}`
                                );
                                this.loading$.next(false);
                                return EMPTY;
                            }),
                        ).subscribe();
                        this.loadCounterparties();
                        break;
                    default:
                        return;
                }
            }
        });
    }

    openItem(item: any) {
        switch (this.data.itemsType) {
            case ListItemType.ASSOCIATION:
                this.sharedService.openDialog(
                    AssociationViewDialogComponent,
                    {...this.config.addItemDialog, maxWidth: '800px', hasBackdrop: true},
                    {
                        association: item,
                        permissions: this.permissions
                    }
                );
                break;
            case ListItemType.CONTRACT:
                this.sharedService.openCrmItem(ListItemType.CONTRACT, item.id);
                this.watchAddEditContractSuccess();
                break;
            default:
                return;
        }
    }

    /** Watch Add Edit Association Success Action */
    watchAddEditAssociationSuccess() {
        this.actions$.pipe(
            ofType(
                associationActions.AssociationsActionTypes.AddAssociationsSuccess,
                associationActions.AssociationsActionTypes.EditAssociationsSuccess,
            ),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loadAssociationsList();
        });
    }

    /** Watch Add or Edit Contact Success */
    watchAddEditContactSuccess() {
        this.actions$.pipe(
            ofType(
                contactsActions.ContactsActionTypes.AddContactSuccess,
                contactsActions.ContactsActionTypes.EditContactSuccess,
            ),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loadContactsList();
        });
    }

    /** Watch Add or Edit Project Success */
    watchAddEditProjectSuccess() {
        this.actions$.pipe(
            ofType(
                projectsActions.ProjectsActionTypes.AddProjectsSuccess,
                projectsActions.ProjectsActionTypes.EditProjectsSuccess,
            ),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loadProjectsList();
        });
    }

    /** Watch Add or Edit Task Success */
    watchAddEditTaskSuccess() {
        this.actions$.pipe(
            ofType(
                leadsBoardTasksActions.LeadsBoardActionTypes.AddTasksSuccess,
                leadsBoardTasksActions.LeadsBoardActionTypes.EditTasksSuccess,
            ),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loadTasksList();
        });
    }

    watchAddEditContractSuccess() {
        this.actions$.pipe(
            ofType(contractActions.ContractsActionTypes.AddContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: contractActions.AddContractsSuccess) => {
            this.closeDialog(true, action.payload);
        });

        this.actions$.pipe(
            ofType(contractActions.ContractsActionTypes.EditContractsSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: contractActions.EditContractsSuccess) => {
            this.closeDialog(true, action.payload);
        });
    }

    /** Watch Delete Success Actions */
    watchDeleteSuccess() {
        this.actions$.pipe(
            ofType(
                contactsActions.ContactsActionTypes.DeleteContactSuccess,
            ),
            takeUntil(this.destroyed$),
        ).subscribe((
            action: contactsActions.DeleteContactSuccess
        ) => {
            if (action.type === '[Contacts] Delete Contact Success') {
                this.loadContactsList();
            }
        });
    }

    selectItem(result: boolean, data?: any) {
        if (this.data.itemsType === ListItemType.LEAD) {
            this.loading$.next(true);
            this.crmService.createTaskFromLead(data.id).pipe(
                takeUntil(this.destroyed$),
                map((response: TaskResponse) => {
                    this.loading$.next(false);
                    this.closeDialog(true, response);
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.message}`
                    );
                    this.loading$.next(false);
                    return EMPTY;
                })
            ).subscribe();
        } else {
            this.closeDialog(result, data);
        }
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }

    isItemSelected(item: any): boolean {
        return this.selectedItems.some(selected => selected.id == item.id);
    }

    toggleSelectItem(item: any) {
        this.selectedItems = this.isItemSelected(item)
            ? this.selectedItems.filter(selected => selected.id != item.id)
            : [...this.selectedItems, item];
        this.changeDetectorRef.markForCheck();
    }

    get allVisibleSelected(): boolean {
        return !!this.dataSource.data.length && this.dataSource.data.every((item: any) => this.isItemSelected(item));
    }

    toggleSelectAll() {
        if (this.allVisibleSelected) {
            const visibleIds = new Set(this.dataSource.data.map((item: any) => item.id));
            this.selectedItems = this.selectedItems.filter(selected => !visibleIds.has(selected.id));
        } else {
            const newItems = this.dataSource.data.filter((item: any) => !this.isItemSelected(item));
            this.selectedItems = [...this.selectedItems, ...newItems];
        }
        this.changeDetectorRef.markForCheck();
    }

    confirmSelection() {
        this.closeDialog(true, this.selectedItems);
    }
}
